import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MessageService } from "primeng/api";
import { debounceTime } from "rxjs/operators";
import { HttpService } from "../../services/http.service";
import { StateService } from "../../services/state.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-share-experiment",
  templateUrl: "./share-experiment.component.html",
  styleUrls: ["./share-experiment.component.scss"],
})
export class ShareExperimentComponent implements OnInit {
  @Input() selectedExperiment: any;
  @Input() isActionFromStudio: boolean = false;
  @Input() hasMultipleShareAction: boolean = false;
  @Output() closeSharePopup: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  mailIdList: any[] = [];
  suggestions: any[] = [];
  visible: boolean = true;
  allowEditing: boolean = true;
  shareLoader: boolean = false;
  expID: any;
  sharedUsers: any[] = [];
  sharedWithClicked: boolean = false;
  removeShareLoader: boolean = false;
  sharedUserIDTobeRemoved: any;
  optionsList = [
    { name: this.translate.instant("PRODUCT_HEADER.VIEW"), value: "view" },
    { name: this.translate.instant("PRODUCT_SIDEBAR.EDIT"), value: "edit" },
    { name: this.translate.instant("PRODUCT_HEADER.REMOVE"), value: "remove" },
  ];
  domainType: string = "";
  validUserList: any = [];

  // For multiple share action
  @Input() multipleSelectedData: any[] = []; // For multiple share action
  @Output() bulkShareActionEmit = new EventEmitter<any>();

  @Input() isActionFromParent: boolean = false; // To identify the action is from parent component or not
  @Input() isAllUsersAllowed: boolean = false; // To identify the user is not key account user
  @Output() shareActionEmit = new EventEmitter<any>();

  constructor(
    private stateService: StateService,
    private httpService: HttpService,
    private messageService: MessageService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.domainType = sessionStorage.getItem("domainType") || "";
  }

  ngOnChanges(changes: SimpleChanges) {
    // If multiple share action is enabled, get the shared users for each selected experiment
    if (
      changes.hasMultipleShareAction &&
      changes.hasMultipleShareAction.currentValue
    ) {
      this.hasMultipleShareAction =
        changes.hasMultipleShareAction.currentValue || false;
      this.sharedUsers = [];
      this.sharedWithClicked = false;

      this.multipleSelectedData.forEach((experiment) => {
        if (experiment) {
          if (!experiment.hasOwnProperty("shared_users")) {
            experiment.shared_users = [];
          }
          this.getSharedUsersList(experiment);
        }
      });
    }
    if (changes.selectedExperiment && changes.selectedExperiment.currentValue) {
      this.expID = changes.selectedExperiment.currentValue.id;
      this.selectedExperiment = changes.selectedExperiment.currentValue;
      if (!this.selectedExperiment.hasOwnProperty("shared_users")) {
        this.selectedExperiment.shared_users = [];
      }
      this.getSharedUsersList(changes.selectedExperiment.currentValue);
    }

    if (changes.isAllUsersAllowed && changes["isAllUsersAllowed"]) {
      this.isAllUsersAllowed = changes.isAllUsersAllowed.currentValue || false;
    }
  }

  closeDialog() {
    this.mailIdList = [];
    this.closeSharePopup.emit(true);
    this.validUserList = [];
    this.sharedWithClicked = false;
    this.isActionFromStudio = false;
    this.hasMultipleShareAction = false;
  }

  getUserSuggestions(event: any) {

    let key_acc_id: any = null;
    let exclude_id: any = null;
    if (!this.hasMultipleShareAction && !this.isAllUsersAllowed) {
      key_acc_id = this.selectedExperiment.key_account_id
        ? this.selectedExperiment.key_account_id
        : sessionStorage.getItem("expKeyAccountId");        
      exclude_id =
        this.selectedExperiment.created_by || this.selectedExperiment.user || this.selectedExperiment.owner_id;
    }

    const query = event.query;

    if (event.originalEvent?.inputType === 'insertFromPaste') {
      const users = this.parseUsers(query);

      if (users.length < 2 && !this.isValidEmail(users[0])) {
        return this.getUserFromServer(query, key_acc_id, exclude_id);
      }

      this.handlePastedUsers(users, key_acc_id, exclude_id);
      return;
    }

    this.getUserFromServer(query, key_acc_id, exclude_id);
  }

  // Helper method to separate pasted user list
  private parseUsers(query: string): string[] {
    return query
      .split(',')
      .map((e: string) => e.trim())
      .filter(Boolean);
  }

  // Helper method to validate and add authorized user 
  handlePastedUsers(users: string[], key_acc_id: string, exclude_id: string) {

    users.forEach((user: string, index: number) => {
      if (users.length - 1 === index) {
        setTimeout(() => {
          const inputEl = document.querySelector(
            '.share-exp-autocomplete input'
          ) as HTMLInputElement;

          if (inputEl) {
            inputEl.value = '';
          }
        }, 800);
      }
      if (this.validUserList.find((e: any) => e.email === user || e.name === user)) return;

      if (!this.isValidEmail(user)) {
        this.addInvalidUser(user);
        return;
      }

      const url = `${this.stateService.apiList.user.searchUserV3}?search=${user}&key_account_id=${key_acc_id}`;

      this.getUserdata(url).subscribe({

        next: (val: any) => {
          const sharedUserIds = new Set(
            (this.sharedUsers || []).map((user: any) => user.id)
          );
          const userData = val
            .filter((each: any) => {
              return (
                each.id !== exclude_id &&
                !sharedUserIds.has(each.id)
              );
            })
            .map((each: any) => {
              each.name = [each.first_name, each.last_name]
                .filter((name) => name)
                .join(" ");
                
              const loggedInUserId = this.stateService.getUser()?.id;
              if (each.id === loggedInUserId) {
                each.has_key_account_access = false;
              } else {
                each.has_key_account_access = key_acc_id === "Other" ||
                  this.hasMultipleShareAction ||
                  this.isAllUsersAllowed ||
                  this.selectedExperiment?.experiment_type === "folder" ?
                  true : this.sharedUsers?.find((e: any) => e.id === each.id) ? false : each.has_key_account_access;
              }
                return each;
            });

          if (userData.length) {
            const data = userData[0];

            data.has_key_account_access =
              data.has_key_account_access &&
              data.email === user.trim().toLowerCase();

            if (!this.validUserList.find((e: any) => e.email === data.email)) {
              this.validUserList = [...this.validUserList, data];
            }

          } else {
            const name = [val[0]?.first_name, val[0]?.last_name].filter(Boolean).join(' ') || user;
            this.addInvalidUser(name);
          }

        },
        error: (err: any) => {
          if (err.status === 400) {
          } else if (err.status !== 401) {
            this.messageService.clear();
            this.messageService.add({
              key: "c",
              severity: "warn",
              summary: err.error.message,
              detail: err.error.traceback,
            });
          }
          this.stateService.removeCalledApi(url);
        }

      });
    });
  }

  getUserdata(url: string) {
    return this.httpService.getMethod(url).pipe(debounceTime(500));
  }

  //Regex to verify email
  isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  //Helper method to enable share button
  areAllValidUser() {
    return !this.validUserList.length || this.validUserList.some((user: any) => !user.has_key_account_access);
  }

  addInvalidUser(user: string) {
    if (this.validUserList.find((e: any) => e.email === user || e.name === user)) return;
    this.validUserList = [
      ...this.validUserList,
      { name: user, has_key_account_access: false }
    ];
  }

  //Helper method to get user details for typed input
  getUserFromServer(query: string, key_acc_id: string, exclude_id: string) {

    const url = `${this.stateService.apiList.user.searchUserV3}?search=${query}&key_account_id=${key_acc_id}`;
    this.httpService
      .getMethod(url)
      .pipe(debounceTime(500))
      .subscribe(
        (val: any) => {
          const loggedInUserId = this.stateService.getUser()?.id;
          const sharedUserIds = new Set(
            (this.sharedUsers || []).map((user: any) => user.id)
          );
          this.mailIdList = val
            .filter((each: any) => {
              return (
                each.id !== exclude_id &&
                each.id !== loggedInUserId &&
                !sharedUserIds.has(each.id)
              );
            })
            .map((each: any) => {
              each.name = [each.first_name, each.last_name]
                .filter((name) => name)
                .join(" ");
              each.has_key_account_access =
                key_acc_id === "Other" || this.hasMultipleShareAction || this.isAllUsersAllowed || this.selectedExperiment?.experiment_type === "folder" ? true : this.sharedUsers?.find((e: any) => e.id === each.id) ? false : each.has_key_account_access;
              return each;
            });
          this.suggestions = this.mailIdList;
        },
        (err: any) => {
          if (err.status === 400) {
          } else if (err.status !== 401) {
            this.messageService.clear();
            this.messageService.add({
              key: "c",
              severity: "warn",
              summary: err.error.message,
              detail: err.error.traceback,
            });
          }
          this.stateService.removeCalledApi(url);
        }
      );
  }

  isSuggestionDisabled(data: any): boolean {
    let userExixts = this.sharedUsers.some((user: any) => user.id === data.id);
    if (userExixts) {
      return userExixts;
    }
    return (
      this.isNotFolderType &&
      !this.hasMultipleShareAction &&
      !this.isAllUsersAllowed &&
      !data?.has_key_account_access
    );
  }

  handleSuggestionClick(event: Event, data: any) {
    let userExixts = this.sharedUsers.some((user: any) => user.id === data.id);
    if (userExixts) {
      return userExixts;
    }

    const isAllowed =
      this.selectedExperiment?.experiment_type === 'folder' ||
      this.hasMultipleShareAction ||
      this.isAllUsersAllowed ||
      data?.has_key_account_access;

    if (!isAllowed) {
      event.stopPropagation();
    }
    if ((!data?.first_name && !data?.last_name) && (this.selectedExperiment?.experiment_type === 'folder' ||
      this.hasMultipleShareAction)) {
      data.name = data.email;
    }    
    return isAllowed;
  }

  async shareInvite() {
    if (!this.validUserList?.length) return;

    this.shareLoader = true;

    try {
      const promises = this.validUserList.map((user: any, index: number) => {
        const userPayload = {
          user_id: user.id,
          access_type: this.allowEditing ? 'edit' : 'read',
          showToast: index === this.validUserList.length - 1,
        };

        if (this.isActionFromParent) {
          this.shareActionEmit.emit({
            data: {
              payload: userPayload,
              userData: user,
            }
          });
          return Promise.resolve();
        }

        if (this.hasMultipleShareAction) {
          this.bulkShareActionEmit.emit(userPayload);
          return Promise.resolve();
        }

        return this.selectedExperiment.experiment_type !== 'folder'
          ? this.shareExperimentApiCall(user)
          : this.shareFloderApiCall(user);
      });

      const results = await Promise.allSettled(promises);

      const allSuccess = results.every(r => r.status === 'fulfilled');

      if (this.hasMultipleShareAction && allSuccess) {
        this.closeDialog();
      }

    } catch (err) {
      console.error('Share failed:', err);
    } finally {
      this.shareLoader = false;
      this.validUserList = [];
    }
  }

  // Helper method to get unique shared users list to avoid duplicate entry in UI when multiple share action is enabled
  get uniqueSharedUsers() {
  const seen = new Set();

  return this.sharedUsers.filter((user: any) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });
}


  // Share the experiment to the selected user Api call
  shareExperimentApiCall(selectedItem: any) {
    const url = `${this.stateService.apiList.experiments.getExperiments}/${this.expID}/share/${selectedItem.id}`;
    const payload = {
      experiment_id: this.expID,
      user_id: selectedItem.id,
      share_type: this.allowEditing ? "edit" : "read",
    };
    this.commonApiCall(
      url,
      "postMethod",
      payload,
      (response: any, params: any) => {
        if (response) {
          this.getCallbackSharedUserResponse(selectedItem);
        }
      }
    );
  }

  // Helper method to callback the shared user response
  getCallbackSharedUserResponse(selectedItem: any) {
    if (this.isActionFromStudio) {
      this.showSuccessToast("Shared successfully");
    } else {
      this.messageService.clear('s');
      this.messageService.add({
        key: "s",
        severity: "success",
        summary: "Shared successfully",
        detail: "Success",
      });
    }
    this.selectedExperiment.shared_users.push(selectedItem.id);
    this.updateSharedUsersList(selectedItem);
  }

  // Helper method to update the shared user list in the UI
  updateSharedUsersList(selectedItem: any) {
    const firstname = selectedItem.name
      ? selectedItem.name.split(" ")[0]
      : selectedItem.first_name;
    const lastname = selectedItem.name
      ? selectedItem.name.split(" ")[1]
      : selectedItem.last_name;
    selectedItem.first_name = firstname;
    selectedItem.last_name = lastname;
    selectedItem.permission = this.allowEditing ? "edit" : "read";
    if (this.sharedUsers) {
      this.sharedUsers.push(selectedItem);
      this.selectedExperiment.shared_users_data = this.sharedUsers;
    }
    this.shareLoader = false;
  }

  getSharedUsersList(experiment: any) {
    if (experiment.shared_users_data || !experiment.shared_users.length) {
      if (this.hasMultipleShareAction) {
        this.sharedUsers.push(
          ...(experiment.shared_users.length
            ? experiment.shared_users_data.map((user: any) => {
                return user;
              })
            : [])
        );
      } else {
        this.sharedUsers = experiment.shared_users.length
          ? experiment.shared_users_data
          : [];
      }
      this.sharedWithClicked = true;
      return;
    }

    const payload = {
      users_list: experiment.shared_users.map((user: any) => user.id || user.user_id),
    };

    this.stateService.getBulkUserDetailsService(payload).subscribe(
      (val: any) => {
        if (val) {
          experiment.shared_users_data = val;
          experiment.shared_users_data.map((user: any) => {
            const index = experiment.shared_users.findIndex(
              (user1: any) => user1.id === user.id || user1.user_id === user.id
            );

            user["permission"] = experiment.shared_users[index].access || experiment.shared_users[index].access_type;
          });
          if (this.hasMultipleShareAction) {
            if (!Array.isArray(this.sharedUsers)) {
              this.sharedUsers = [];
            }
            this.sharedUsers.push(
              ...experiment.shared_users_data.map((user: any) => {
                return user;
              })
            );
          } else {
            this.sharedUsers = experiment.shared_users_data;
          }
          this.sharedWithClicked = true;
        }
      },
      (err: any) => {
        if (err.status === 400) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: "Warn Message",
            detail: err.error.message,
          });
        } else if (err.status !== 401) {
          this.messageService.clear();
          this.messageService.add({
            key: "c",
            severity: "warn",
            summary: err.error.message,
            detail: err.error.traceback,
          });
        }
      }
    );
    if (!this.hasMultipleShareAction) {
      this.showInvite(experiment);
    }
  }

  showInvite(experiment: any) {
    this.expID = experiment.id;
    this.selectedExperiment = experiment;
    this.sharedUsers = experiment.shared_users_data;
  }

  getColor(index: number) {
    let colors = ["#FFDBDB", "#E2DBFF", "#DBF5FF", "#DBE2FF"];
    return colors[index % 4];
  }

  hasValidValue(str: any) {
    return !!str && str.toLowerCase() !== "none";
  }

  // User actions permissions like edit read or remove
  sharedUserActions(userID: string, event: any, item: any) {
    // Code to handle the action from parent component (Dashboard)
    if (this.isActionFromParent) {
      const userPayload: any = {
        user_id: userID,
      };

      if (event.value !== "remove") {
        userPayload["access_type"] = event.value === "edit" ? "edit" : "read";
      } else {
        this.removeShareLoader = true;
        this.sharedUserIDTobeRemoved = userID;
      }

      const data = {
        payload: userPayload,
      };

      this.shareActionEmit.emit({data, isEdit: true});
      return;
    }

    if (this.isNotFolderType) {
      this.sharedExperimentUserActions(userID, event);
    } else {
      this.sharedFolderUserActions(userID, event);
    }
  }

  // Update the experiment access to the selected user Api call
  sharedExperimentUserActions(userID: string, event: any) {
    switch (event.value) {
      case "edit":
        const url = `${this.stateService.apiList.experiments.getExperiments}/${this.expID}/share/${userID}`;
        const payload = {
          share_type: "edit",
        };

        this.httpService.putMethod(url, payload).subscribe((val: any) => {});

        break;
      case "view":
        const permissionUrl = `${this.stateService.apiList.experiments.getExperiments}/${this.expID}/share/${userID}`;
        const data = {
          share_type: "read",
        };

        this.httpService
          .putMethod(permissionUrl, data)
          .subscribe((val: any) => {});
        break;
      case "remove":
        this.sharedUserIDTobeRemoved = userID;
        if (!this.removeShareLoader) {
          this.removeShareLoader = true;
          const url = `${this.stateService.apiList.experiments.getExperiments}/${this.expID}/share/${userID}`;
          this.httpService.deleteMethod(url).subscribe(
            (val: any) => {
              this.getCallbackRemovedUserResponse(userID);
            },
            (err: any) => {
              this.sharedUserIDTobeRemoved = null;
              this.removeShareLoader = false;
              if (err.status === 400) {
                this.messageService.add({
                  key: "w",
                  severity: "warn",
                  summary: "Warn Message",
                  detail: err.error.message.message,
                });
              } else if (err.status !== 401) {
                this.messageService.clear();
                this.messageService.add({
                  key: "c",
                  severity: "warn",
                  summary: err.error.message,
                  detail: err.error.traceback,
                });
              }
            }
          );
        }
        break;
      default:
        break;
    }
  }

  // Helper method to removed user from shared user list once Api call success
  getCallbackRemovedUserResponse(userID: string) {
    this.updateRemovedUserList(userID);

    if (this.isActionFromStudio) {
      this.showSuccessToast("Share access removed successfully");
    } else {
      this.messageService.add({
        key: "s",
        severity: "success",
        summary: "Share access removed successfully",
        detail: "Success",
      });
    }
  }

  // Helper method to update the shared user list in the UI after removing the user
  updateRemovedUserList(userID: string) {
    if (this.sharedUsers) {
      // Remove the user from the shared_users
      for (let i = 0; i <= this.sharedUsers.length; i++) {
        if (this.sharedUsers[i].id === userID) {
          this.sharedUsers.splice(i, 1);
          break;
        }
      }
    }

    let currentUsers = this.selectedExperiment.shared_users;
    for (let i = 0; i <= currentUsers.length; i++) {
      if (currentUsers[i] === userID) {
        currentUsers.splice(i, 1);
        break;
      }
    }

    this.selectedExperiment.shared_users = currentUsers;
    this.removeShareLoader = false;
    this.sharedUserIDTobeRemoved = null;
  }

  // Function to share the folder to the selected user Api call
  shareFloderApiCall(selectedItem: any) {
    const url = `${this.stateService.apiList.experiments.shareAndUpdateFolder(this.expID)}`;
    const payload = {
      user_id: selectedItem.id,
      access_type: this.allowEditing ? "edit" : "read",
    };
    this.commonApiCall(
      url,
      "postMethod",
      payload,
      (response: any, params: any) => {
        if (response) {
          this.getCallbackSharedUserResponse(selectedItem);
        }
      }
    );
  }

  // Function to update the user access and revoked the user for selected floder
  sharedFolderUserActions(userID: string, event: any) {
    const url = `${this.stateService.apiList.experiments.shareAndUpdateFolder(this.expID)}`;
    switch (event.value) {
      case "edit":
      case "view":
        const payload = {
          user_id: userID,
          access_type: event.value === "edit" ? "edit" : "read",
        };
        this.httpService.patchMethod(url, payload).subscribe((val: any) => {});
        break;

      case "remove":
        this.sharedUserIDTobeRemoved = userID;
        if (!this.removeShareLoader) {
          this.removeShareLoader = true;
          const payload = {
            user_id: userID,
          };

          this.commonApiCall(
            url,
            "deleteMethod",
            payload,
            (response: any, params: any) => {
              this.getCallbackRemovedUserResponse(userID);
            }
          );
        }
        break;
      default:
        break;
    }
  }

  get isNotFolderType() {
    return (
      this.selectedExperiment &&
      this.selectedExperiment?.experiment_type !== "folder"
    );
  }

  showSuccessToast(message: string) {
    this.messageService.clear();
    this.messageService.add({
      key: "sbc",
      severity: "success",
      summary: message,
      detail: "Success",
      life: 5000,
    });
  }

  showWarningToast(message: string) {
    this.messageService.clear();
    this.messageService.add({
      key: "wbc",
      severity: "warn",
      summary: message,
      detail: "Warning",
      life: 5000,
    });
  }

  // Function for Common Api call
  commonApiCall(
    apiUrl: string,
    method: "getMethod" | "postMethod" | "putMethod" | "deleteMethod",
    params: any,
    callback: (response: any, params: any) => void
  ) {
    this.httpService[method](apiUrl, params).subscribe(
      (response: any) => {
        // common callback function for response
        callback(response, params);
      },
      (err: any) => {
        console.error("Error in API call:", err);
        this.messageService.clear();
        if (err.status === 400) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: "Something went wrong!",
            detail: err.error.message,
            sticky: true,
          });
        } else if (err.status === 422) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: err.error.detail[0].msg || "Something went wrong!",
            detail: err.error.detail,
            sticky: true,
          });
        } else if (err.status !== 401) {
          if (this.isActionFromStudio) {
            this.showWarningToast(err.error.message || "Something went wrong!");
          } else {
            this.messageService.add({
              key: "c",
              severity: "warn",
              summary: err.error.message || "Something went wrong!",
              detail: err.error.traceback,
              sticky: true,
            });
          }
        }
        this.shareLoader = false;
        this.removeShareLoader = false;
      }
    );
  }
}
